import React from "react";

/**
 * Compact legal-body renderer.
 *
 * Every NaturePulse legal document keeps its section body in this
 * markdown-lite string format so we can maintain long docs (30-40
 * sections each) without an explosion of JSX. The renderer walks the
 * string once and produces <p>, <ul>/<li>, <h3>, and mailto/link
 * inline elements.
 *
 * Syntax:
 *   Blank line              → paragraph break
 *   Line starting with "- " → list item (contiguous items = <ul>)
 *   Line starting with "## "→ inline sub-heading (<h3>)
 *   Plain lines             → paragraph text (multiple lines joined by space)
 *
 * Inline transforms:
 *   [label](https://url) or [label](/path) → <a>
 *   Bare email addresses → mailto: <a>
 */
export function renderLegalBody(text) {
  if (!text) return null;
  const raw = String(text).trim().split(/\n/);
  const nodes = [];
  let bulletBuf = [];
  let paraBuf = [];

  const flushBullets = () => {
    if (!bulletBuf.length) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`}>
        {bulletBuf.map((li, i) => <li key={i}>{renderInline(li)}</li>)}
      </ul>
    );
    bulletBuf = [];
  };
  const flushPara = () => {
    if (!paraBuf.length) return;
    const joined = paraBuf.join(" ").trim();
    if (joined) nodes.push(<p key={`p-${nodes.length}`}>{renderInline(joined)}</p>);
    paraBuf = [];
  };

  for (const line of raw) {
    const t = line.trimEnd();
    if (!t.trim()) { flushBullets(); flushPara(); continue; }
    if (t.startsWith("## ")) {
      flushBullets(); flushPara();
      nodes.push(<h3 key={`h3-${nodes.length}`}>{renderInline(t.slice(3).trim())}</h3>);
      continue;
    }
    if (t.startsWith("- ")) {
      flushPara();
      bulletBuf.push(t.slice(2).trim());
      continue;
    }
    flushBullets();
    paraBuf.push(t.trim());
  }
  flushBullets();
  flushPara();
  return <>{nodes}</>;
}

// [label](href) + bare emails → clickable
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const EMAIL_RE = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g;

function renderInline(text) {
  const out = [];
  let last = 0;
  let m;
  // markdown links first
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) out.push(...splitEmails(text.slice(last, m.index), out.length));
    const label = m[1], href = m[2];
    const external = /^https?:/.test(href);
    out.push(
      <a
        key={`lnk-${out.length}`}
        href={href}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      >{label}</a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...splitEmails(text.slice(last), out.length));
  return out;
}

function splitEmails(chunk, startKey) {
  const out = [];
  let last = 0;
  let m;
  EMAIL_RE.lastIndex = 0;
  while ((m = EMAIL_RE.exec(chunk)) !== null) {
    if (m.index > last) out.push(chunk.slice(last, m.index));
    out.push(
      <a key={`em-${startKey}-${out.length}`} href={`mailto:${m[1]}`}>{m[1]}</a>
    );
    last = m.index + m[0].length;
  }
  if (last < chunk.length) out.push(chunk.slice(last));
  return out;
}

/**
 * Given a compact list of sections
 *   [{ id, title, body: "raw text..." }, ...]
 * return the same list with body wrapped in the JSX renderer.
 */
export function buildLegalSections(sections) {
  return sections.map((s) => ({ ...s, body: renderLegalBody(s.body) }));
}
